/**
 * NotificationBell - Enhanced bell notification component for header
 *
 * Displays notification count badge and manages dropdown menu
 * Integrates with MessagingContext for real-time updates
 */

import React, { useCallback, useState } from 'react'
import { Notifications } from '@mui/icons-material'
import { Badge } from '@mui/material'

import { useMessaging } from '../../contexts/MessagingContext'

import NotificationDropdown from './NotificationDropdown'
import { formatUnreadCount, UNREAD_COUNT_CEILING } from './unreadCount'

interface NotificationBellProps {
  className?: string
}

/**
 * NotificationBell - Bell icon with badge count and dropdown functionality
 */
// @purpose Header bell icon showing unread notification count and toggling the notification dropdown
export const NotificationBell = ({
  className = '',
}: NotificationBellProps): React.JSX.Element => {
  const { unreadCount } = useMessaging()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = Boolean(anchorEl)
  // One formatting shared by the badge, the aria-label and the dropdown header.
  const unreadLabel = formatUnreadCount(unreadCount)

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <>
      {/* Plain button (not MUI IconButton) so the shape/hover matches the
          sibling theme/settings/help buttons in the header — IconButton
          forces a circular hover; these use a rounded-square. */}
      <button
        type="button"
        onClick={handleOpen}
        className={`p-2 text-header-icon hover:text-white hover:bg-white/10 rounded-lg transition-colors ${className}`}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadLabel} unread)` : ''}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        data-testid="notification-bell"
      >
        <Badge
          // Pre-formatted, so `max` below is INERT (MUI only caps a numeric
          // `badgeContent`). It is kept, fed from the same constant, so a revert
          // to a numeric badge still caps at the same number.
          badgeContent={unreadLabel}
          color="error"
          max={UNREAD_COUNT_CEILING}
          invisible={unreadCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.75rem',
              minWidth: '18px',
              height: '18px',
              borderRadius: '9px',
            },
          }}
        >
          <Notifications sx={{ fontSize: 24 }} />
        </Badge>
      </button>

      <NotificationDropdown
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
      />
    </>
  )
}

export default NotificationBell
